import { collection, query, where, orderBy, getDocs, documentId } from 'firebase/firestore';
import { db } from '../firebase/config';
import { generateDateID } from './dayPostService';

/**
 * Calculate intensity score for a single day based on log ID counts
 * Scoring rules:
 * - trainingLogIDs: 1 per ID
 * - recoveryLogIDs: 1 per ID
 * - niggleLogIDs: ONLY 1 total (regardless of count)
 * - performanceLogIDs: 1 per ID
 * - healthLogIDs: ONLY 1 total (regardless of count)
 * - stepLogIDs: 0 (not counted)
 * - noteLogIDs: 0 (not counted)
 * - wellnessLogIDs: ONLY 1 total (regardless of count)
 * 
 * @param dayPostRefs - DayPostReferences data (contains log IDs, not full logs)
 * @returns Intensity score (0-7, capped)
 */
export function calculateDayIntensity(dayPostRefs: any): number {
  if (!dayPostRefs) return 0;
  
  let score = 0;
  
  // 1. Training Logs: 1 point per ID
  score += (dayPostRefs.trainingLogIDs?.length || 0);
  
  // 2. Recovery Logs: 1 point per ID
  score += (dayPostRefs.recoveryLogIDs?.length || 0);
  
  // 3. Niggle Logs: ONLY 1 point total (regardless of count)
  if (dayPostRefs.niggleLogIDs && dayPostRefs.niggleLogIDs.length > 0) {
    score += 1;
  }
  
  // 4. Performance Logs: 1 point per ID
  score += (dayPostRefs.performanceLogIDs?.length || 0);
  
  // 5. Health Logs: ONLY 1 point total (regardless of count)
  if (dayPostRefs.healthLogIDs && dayPostRefs.healthLogIDs.length > 0) {
    score += 1;
  }
  
  // 6. Step Logs: 0 points (NOT COUNTED)
  // (intentionally omitted)
  
  // 7. Note Logs: 0 points (NOT COUNTED)
  // (intentionally omitted)
  
  // 8. Wellness Logs: ONLY 1 point total (regardless of count)
  if (dayPostRefs.wellnessLogIDs && dayPostRefs.wellnessLogIDs.length > 0) {
    score += 1;
  }
  
  // Cap intensity at 7 for color scale
  return Math.min(score, 7);
}

/**
 * Fetch heatmap data for a user for a specific calendar year.
 * Document IDs ARE the dateIDs (YYYYMMDD), so we use a documentId() range
 * filter — no composite index required.
 *
 * @param userId - User ID
 * @param year   - Calendar year (e.g. 2025, 2026). Defaults to current year.
 * @returns Object containing intensity map and raw dayPost references
 */
export async function fetchHeatmapData(
  userId: string,
  year?: number
): Promise<{
  intensityMap: Map<string, number>;
  dayPosts: any[];
}> {
  try {
    const today = new Date();
    const targetYear = year ?? today.getFullYear();
    const isCurrentYear = targetYear === today.getFullYear();

    const yearStart = new Date(targetYear, 0, 1);
    // For past years fetch through Dec 31; for the current year fetch through today.
    const yearEnd = isCurrentYear ? today : new Date(targetYear, 11, 31);

    const startDateID = generateDateID(yearStart); // e.g. "20250101"
    const endDateID   = generateDateID(yearEnd);   // e.g. "20251231" or today

    // console.log(`[HeatmapService] Fetching data for ${userId} year=${targetYear} (${startDateID} → ${endDateID})`);

    // documentId() filters by the doc's own ID (YYYYMMDD) — no composite
    // index required and works regardless of whether dateID is a stored field.
    const dayPostsRef = collection(db, 'dayPosts', userId, 'posts');
    const q = query(
      dayPostsRef,
      orderBy(documentId()),
      where(documentId(), '>=', startDateID),
      where(documentId(), '<=', endDateID)
    );

    const querySnapshot = await getDocs(q);

    // console.log(`[HeatmapService] Fetched ${querySnapshot.size} dayPosts`);

    const dayPostRefs: any[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // doc.id is always the YYYYMMDD string; use it as the authoritative dateID
      dayPostRefs.push({
        dateID: doc.id,
        trainingLogIDs:    data.trainingLogIDs    || [],
        recoveryLogIDs:    data.recoveryLogIDs    || [],
        niggleLogIDs:      data.niggleLogIDs      || [],
        performanceLogIDs: data.performanceLogIDs || [],
        healthLogIDs:      data.healthLogIDs      || [],
        stepLogIDs:        data.stepLogIDs        || [],
        noteLogIDs:        data.noteLogIDs        || [],
        wellnessLogIDs:    data.wellnessLogIDs    || [],
        activityLogIDs:    data.activityLogIDs    || [],
        femaleCycleLogIDs: data.femaleCycleLogIDs || [],
        peakScoreLogID:    data.peakScoreLogID,
        sessionIDs:        data.sessionIDs        || [],
      });
    });

    // console.log(`[HeatmapService] Sample dayPost:`, dayPostRefs[0]);
    
    // Calculate intensity for each day based on log ID counts
    const intensityMap = new Map<string, number>();
    dayPostRefs.forEach((dayPostRef) => {
      const intensity = calculateDayIntensity(dayPostRef);
      intensityMap.set(dayPostRef.dateID, intensity);
      
      // Log days with activity for debugging
      if (intensity > 0) {
        // console.log(`[HeatmapService] ${dayPostRef.dateID}: intensity ${intensity}`);
      }
    });
    
    // console.log(`[HeatmapService] Calculated intensity for ${intensityMap.size} days, ${Array.from(intensityMap.values()).filter(v => v > 0).length} have activity`);
    
    return {
      intensityMap,
      dayPosts: dayPostRefs as any[] // Return references instead of full DayPost objects
    };
    
  } catch (error) {
    console.error('[HeatmapService] Error fetching heatmap data:', error);
    throw new Error('Failed to fetch heatmap data');
  }
}

/**
 * Transform intensity map to 2D grid for heatmap display
 * Grid format: [week][day] where day 0 = Sunday, 6 = Saturday
 * 
 * @param intensityMap - Map of dateID to intensity
 * @param weeks - Number of weeks to generate (26 or 52)
 * @returns 2D array [weeks][7 days]
 */
export function transformTo2DGrid(
  intensityMap: Map<string, number>,
  weeks: number
): number[][] {
  const grid: number[][] = [];
  const today = new Date();
  
  // Calculate start date (weeks * 7 days ago)
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - (weeks * 7 - 1));
  
  // Generate grid week by week
  for (let weekIndex = 0; weekIndex < weeks; weekIndex++) {
    const weekData: number[] = [];
    
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + (weekIndex * 7 + dayIndex));
      
      const dateID = generateDateID(currentDate);
      const intensity = intensityMap.get(dateID) || 0;
      
      weekData.push(intensity);
    }
    
    grid.push(weekData);
  }
  
  return grid;
}
